import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronLeft, ChevronRight, Eye, RefreshCw } from 'lucide-react';
import MainService from '../../services/MainService';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPageHeading } from '../../locales/navigation';
import { getAppUi } from '../../locales/appUi';

const PAGE_SIZE = 20;

const OrderList = () => {
  const { language } = useLanguage();
  const ui = useMemo(() => getAppUi(language), [language]);
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [voucherNo, setVoucherNo] = useState('');
  const [keyword, setKeyword] = useState('');

  const loadOrders = async (pageNum = 1, resetPage = false) => {
    try {
      setLoading(true);
      const currentPage = resetPage ? 1 : pageNum;
      const skip = (currentPage - 1) * PAGE_SIZE;
      const response = await MainService.GetOrderList({
        Skip: skip,
        Take: PAGE_SIZE,
        VoucherNo: voucherNo.trim() || undefined,
        Keyword: keyword.trim() || undefined,
      });
      const data = response?.Data ?? response?.data ?? [];
      const totalCount = response?.Total ?? response?.total ?? 0;
      setOrders(Array.isArray(data) ? data : []);
      setTotal(typeof totalCount === 'number' ? totalCount : 0);
      if (resetPage) setPage(1);
      else setPage(currentPage);
    } catch (err) {
      console.error('Error loading orders:', err);
      setOrders([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders(page);
  }, [page]);

  const handleSearch = () => {
    loadOrders(1, true);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);

  const formatAddress = (addr) => {
    if (!addr) return '-';
    const parts = [addr.Address, addr.Location?.Name, addr.Emirate?.Name].filter(Boolean);
    return parts.length ? parts.join(', ') : '-';
  };

  const formatDate = (d) => {
    if (d == null || d === '') return '-';
    let date;
    if (typeof d === 'object' && d.Day != null && d.Month != null && d.Year != null) {
      date = new Date(d.Year, d.Month - 1, d.Day);
    } else if (typeof d === 'string') {
      date = new Date(d);
    } else if (d instanceof Date) {
      date = d;
    } else {
      return '-';
    }
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  const formatCurrency = (n) => {
    if (n == null || n === '') return '-';
    const num = typeof n === 'number' ? n : parseFloat(n);
    return isNaN(num) ? '-' : `AED ${num.toFixed(2)}`;
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">{getPageHeading(language, 'orderList')}</h1>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{ui('columns.voucherNo')}</label>
            <input
              type="text"
              value={voucherNo}
              onChange={(e) => setVoucherNo(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder={ui('orders.orderList.voucherPlaceholder')}
              className="w-40 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{ui('orders.orderList.keyword')}</label>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder={ui('orders.orderList.keywordPlaceholder')}
              className="w-56 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm"
            />
          </div>
          <button
            onClick={handleSearch}
            disabled={loading}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-primary/50 flex items-center gap-2 disabled:opacity-70"
          >
            <Search className="w-4 h-4" />
            {ui('common.search')}
          </button>
          <button
            onClick={() => loadOrders(page)}
            disabled={loading}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2 disabled:opacity-70"
            title={ui('common.refresh')}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            {ui('common.refresh')}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading && orders.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <RefreshCw className="w-8 h-8 text-primary animate-spin" />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.voucherNo')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.clientBillNo')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.batchNo')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.client')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.customer')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.deliveryAddress')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.orderDate')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.deliveryDate')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.product')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.deliveryCh')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.total')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('common.status')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.payType')}</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.action')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {orders.length === 0 ? (
                    <tr>
                      <td colSpan="14" className="px-4 py-8 text-center text-gray-500">
                        {ui('orders.orderList.noOrders')}
                      </td>
                    </tr>
                  ) : (
                    orders.map((order) => (
                      <tr key={order.Id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">{order.VoucherNo || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{order.ClientBillNo || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{order.BatchNo || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {order.Client?.Name || '-'}
                          {order.Client?.MobileNo1 && <span className="block text-xs text-gray-400">{order.Client.MobileNo1}</span>}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {order.Customer?.Name || '-'}
                          {order.Customer?.MobileNo1 && <span className="block text-xs text-gray-400">{order.Customer.MobileNo1}</span>}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 max-w-[200px]" title={formatAddress(order.CustomerAddress)}>
                          {formatAddress(order.CustomerAddress)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{formatDate(order.Date)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{formatDate(order.DeliveryDate)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap text-right">{formatCurrency(order.ProductPrice ?? order.ProductAmount)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap text-right">{formatCurrency(order.DeliveryCharge)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap text-right font-medium">{formatCurrency(order.TotalAmount ?? order.Total ?? order.CustomerAmount)}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {order.Status && (
                            <span
                              className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full"
                              style={{
                                backgroundColor: order.Status.Color ? `${order.Status.Color}20` : '#e5e7eb',
                                color: order.Status.Color || '#374151',
                              }}
                            >
                              {order.Status.Name}
                            </span>
                          )}
                          {!order.Status && '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{order.PayType || '-'}</td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <button
                            onClick={() => navigate(`/home/orders/${order.Id}`)}
                            className="inline-flex items-center gap-1 text-primary hover:text-green-700 text-sm font-medium"
                            title={ui('common.viewDetails')}
                          >
                            <Eye className="w-4 h-4" />
                            {ui('common.view')}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {total > 0 && (
              <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between flex-wrap gap-2">
                <p className="text-sm text-gray-600">
                  {ui('orders.orderList.showingToOf', { from, to, total })}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1 || loading}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    {ui('common.previous')}
                  </button>
                  <span className="px-3 py-1.5 text-sm text-gray-600">
                    {ui('orders.orderList.pageOf', { page, totalPages })}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages || loading}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    {ui('common.next')}
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default OrderList;
